/**
 * 班级「家长反馈」(两周一次,博颂 2026-09-24)。
 * AI 按这段时间的作业、考试、课堂诊断、行规分、课堂记录起草一段话 → 老师改 →
 * 发布。发布后家长在钉钉里的「孩子在校情况」能看到。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import {
  generateFeedback,
  runPool,
  useClassFeedback,
  useFeedbackMutations,
  type FeedbackRow,
} from "../../api/lessons";
import { useClassWithRoster } from "../../api/school";

const today = () => new Date().toISOString().slice(0, 10);

export function ClassFeedbackPage(): JSX.Element {
  const { t } = useTranslation();
  const { classId = "" } = useParams();
  const klass = useClassWithRoster(classId);
  const [periodEnd, setPeriodEnd] = useState(today());
  const [days, setDays] = useState(14);
  const [hint, setHint] = useState("");
  const list = useClassFeedback(classId, periodEnd);
  const m = useFeedbackMutations(classId);
  const [busy, setBusy] = useState<Record<string, "running" | "no_data" | "error">>({});
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function generate(ids: string[]) {
    setErr(null);
    setProgress({ done: 0, total: ids.length });
    await runPool(ids, 3, async (id) => {
      setBusy((b) => ({ ...b, [id]: "running" }));
      try {
        const out = await generateFeedback(classId, id, { period_end: periodEnd, days, hint: hint.trim() || null });
        setBusy((b) => {
          const n = { ...b };
          if (out.status === "no_data") n[id] = "no_data";
          else delete n[id];
          return n;
        });
      } catch (e) {
        setBusy((b) => ({ ...b, [id]: "error" }));
        setErr((e as Error).message);
      }
      setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      m.invalidate();
    });
    setProgress(null);
  }

  const students = list.data?.students ?? [];
  const pending = students.filter((s) => !s.feedback).map((s) => s.student_id);
  const drafts = students.filter((s) => s.feedback?.status === "draft");

  return (
    <div>
      <Link to={`/classes/${classId}`} className="text-sm text-sky-600 hover:underline">{t("gpa.grades.back")}</Link>
      <header className="mb-4 mt-1">
        <h1 className="text-2xl font-semibold text-slate-800">{klass.data?.name ?? ""} · {t("feedback.title")}</h1>
        <p className="text-sm text-slate-500">{t("feedback.hint")}</p>
      </header>

      <section className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
        <label className="text-xs text-slate-600">
          {t("feedback.periodEnd")}
          <br />
          <input id="fb-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="rounded border border-slate-200 px-2 py-1" />
        </label>
        <label className="text-xs text-slate-600">
          {t("feedback.days")}
          <br />
          <select id="fb-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded border border-slate-200 px-2 py-1">
            {[7, 14, 21, 28].map((d) => <option key={d} value={d}>{t("feedback.nDays", { n: d })}</option>)}
          </select>
        </label>
        <label className="min-w-[16rem] flex-1 text-xs text-slate-600">
          {t("feedback.hintLabel")}
          <br />
          <input id="fb-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder={t("feedback.hintPh")} className="w-full rounded border border-slate-200 px-2 py-1" />
        </label>
        <button type="button" disabled={!!progress || pending.length === 0} onClick={() => void generate(pending)} className="rounded bg-sky-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-600 disabled:opacity-50" data-testid="fb-generate-all">
          {progress ? t("feedback.generating", progress) : t("feedback.generateAll", { n: pending.length })}
        </button>
        <button
          type="button"
          disabled={drafts.length === 0 || !!progress}
          onClick={async () => { for (const s of drafts) await m.publish.mutateAsync(s.feedback!.id); }}
          className="rounded border border-emerald-300 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
        >
          {t("feedback.publishAll", { n: drafts.length })}
        </button>
      </section>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      {list.isLoading && <p className="text-sm text-slate-500">{t("common.loading")}</p>}

      <div className="flex flex-col gap-3">
        {students.map((s) => (
          <FeedbackCard key={s.student_id} row={s} state={busy[s.student_id]} onRegenerate={() => void generate([s.student_id])} m={m} />
        ))}
      </div>
    </div>
  );
}

function FeedbackCard({ row, state, onRegenerate, m }: { row: FeedbackRow; state?: "running" | "no_data" | "error"; onRegenerate: () => void; m: ReturnType<typeof useFeedbackMutations> }) {
  const { t } = useTranslation();
  const f = row.feedback;
  const [text, setText] = useState(f?.content ?? "");
  useEffect(() => setText(f?.content ?? ""), [f?.content]);
  const dirty = !!f && text.trim() !== f.content;
  const [notified, setNotified] = useState<number | null>(null);
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="fb-card">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-slate-800">{row.name_zh}</span>
        {row.name_en && <span className="text-xs text-slate-400">{row.name_en}</span>}
        <span className={`text-xs ${row.guardians ? "text-emerald-700" : "text-slate-400"}`}>
          {row.guardians ? t("feedback.guardians", { n: row.guardians }) : t("feedback.noGuardian")}
        </span>
        {row.last_published_end && <span className="text-xs text-slate-400">{t("feedback.lastSent", { d: row.last_published_end })}</span>}
        <span className="ml-auto flex items-center gap-2 text-xs">
          {f && (
            <span className={`rounded px-2 py-0.5 ${f.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {t(`feedback.status.${f.status}`)}{f.edited ? ` · ${t("feedback.edited")}` : ""}
            </span>
          )}
        </span>
      </div>
      {state === "running" && <p className="text-sm text-slate-500">{t("feedback.running")}</p>}
      {state === "no_data" && <p className="text-sm text-slate-400">{t("feedback.noData")}</p>}
      {state === "error" && <p className="text-sm text-rose-600">{t("feedback.failed")}</p>}
      {f && state !== "running" && (
        <textarea
          id={`fb-${row.student_id}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm leading-relaxed"
        />
      )}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 text-xs">
        {!!notified && <span className="text-emerald-700">{t("gpa.publish.notified", { n: notified })}</span>}
        <button
          type="button"
          onClick={() => {
            // 重新起草 = 未经老师审阅的 AI 文字:已发布的会被退回草稿、老师的修改会被覆盖 —— 先问一句。
            // 没动过的草稿直接重写,不打扰。
            if (f && f.status === "published" && !window.confirm(t("feedback.regenConfirmPublished"))) return;
            if (f && f.status !== "published" && (f.edited || dirty) && !window.confirm(t("feedback.regenConfirmEdited"))) return;
            onRegenerate();
          }}
          disabled={state === "running"} className="rounded border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50 disabled:opacity-40">
          {f ? t("feedback.regenerate") : t("feedback.generate")}
        </button>
        {f && (
          <>
            <button type="button" disabled={!dirty} onClick={() => m.save.mutate({ id: f.id, content: text.trim() })} className="rounded border border-sky-200 px-2 py-1 text-sky-600 hover:bg-sky-50 disabled:opacity-40">
              {t("common.save")}
            </button>
            {f.status === "draft" ? (
              <button type="button" disabled={dirty} title={dirty ? t("feedback.saveFirst") : undefined} onClick={() => m.publish.mutate(f.id, { onSuccess: (r) => setNotified(r.notified ?? null) })} className="rounded bg-emerald-600 px-2 py-1 font-medium text-white hover:bg-emerald-700 disabled:opacity-40">
                {t("feedback.publish")}
              </button>
            ) : (
              <button type="button" onClick={() => m.unpublish.mutate(f.id)} className="rounded border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50">
                {t("feedback.unpublish")}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
